<template>
  <teleport to="body">
    <div
      aria-live="polite"
      aria-label="Meldingen"
      class="fixed top-4 right-4 z-[9999] flex flex-col gap-3 max-w-sm w-full pointer-events-none"
    >
      <TransitionGroup
        enter-active-class="transform ease-out duration-300 transition"
        enter-from-class="translate-x-full opacity-0"
        enter-to-class="translate-x-0 opacity-100"
        leave-active-class="transition ease-in duration-200"
        leave-from-class="translate-x-0 opacity-100"
        leave-to-class="translate-x-full opacity-0"
      >
        <div
          v-for="toast in toasts"
          :key="toast.id"
          :class="[
            'pointer-events-auto w-full rounded-lg shadow-lg overflow-hidden',
            'border',
            toastStyles[toast.type].container
          ]"
          role="alert"
          :aria-labelledby="`toast-title-${toast.id}`"
          :aria-describedby="`toast-message-${toast.id}`"
        >
          <div class="p-4">
            <div class="flex items-start gap-3">
              <!-- Icon -->
              <div :class="['flex-shrink-0', toastStyles[toast.type].icon]">
                <component :is="getIcon(toast.type)" class="h-5 w-5" />
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <p
                  v-if="toast.title"
                  :id="`toast-title-${toast.id}`"
                  :class="['text-sm font-medium', toastStyles[toast.type].title]"
                >
                  {{ toast.title }}
                </p>
                <p
                  :id="`toast-message-${toast.id}`"
                  :class="['text-sm', toast.title ? 'mt-1' : '', toastStyles[toast.type].message]"
                >
                  {{ toast.message }}
                </p>
              </div>

              <!-- Close button -->
              <button
                v-if="toast.dismissible"
                @click="removeToast(toast.id)"
                :class="[
                  'flex-shrink-0 rounded-md p-1.5 inline-flex focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors',
                  toastStyles[toast.type].closeButton
                ]"
                :aria-label="`Melding sluiten: ${toast.message}`"
              >
                <XMarkIcon class="h-4 w-4" />
              </button>
            </div>
          </div>

          <!-- Progress bar -->
          <div class="h-1 w-full" :class="toastStyles[toast.type].progressBg">
            <div
              class="h-full transition-all ease-linear"
              :class="toastStyles[toast.type].progress"
              :style="{
                width: '100%',
                animation: `shrink ${getDuration(toast.type)}ms linear forwards`
              }"
            />
          </div>
        </div>
      </TransitionGroup>
    </div>
  </teleport>
</template>

<script setup>
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon
} from '@heroicons/vue/24/outline'
import { useToast, TOAST_TYPES } from '@/composables/ui/useToast'

const { toasts, removeToast } = useToast()

// Styles per toast type
const toastStyles = {
  [TOAST_TYPES.SUCCESS]: {
    container: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800',
    icon: 'text-green-500 dark:text-green-400',
    title: 'text-green-800 dark:text-green-200',
    message: 'text-green-700 dark:text-green-300',
    closeButton:
      'text-green-500 hover:bg-green-100 dark:hover:bg-green-800/50 focus:ring-green-500',
    progressBg: 'bg-green-100 dark:bg-green-800/30',
    progress: 'bg-green-500 dark:bg-green-400'
  },
  [TOAST_TYPES.ERROR]: {
    container: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800',
    icon: 'text-red-500 dark:text-red-400',
    title: 'text-red-800 dark:text-red-200',
    message: 'text-red-700 dark:text-red-300',
    closeButton: 'text-red-500 hover:bg-red-100 dark:hover:bg-red-800/50 focus:ring-red-500',
    progressBg: 'bg-red-100 dark:bg-red-800/30',
    progress: 'bg-red-500 dark:bg-red-400'
  },
  [TOAST_TYPES.WARNING]: {
    container: 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800',
    icon: 'text-amber-500 dark:text-amber-400',
    title: 'text-amber-800 dark:text-amber-200',
    message: 'text-amber-700 dark:text-amber-300',
    closeButton:
      'text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-800/50 focus:ring-amber-500',
    progressBg: 'bg-amber-100 dark:bg-amber-800/30',
    progress: 'bg-amber-500 dark:bg-amber-400'
  },
  [TOAST_TYPES.INFO]: {
    container: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800',
    icon: 'text-blue-500 dark:text-blue-400',
    title: 'text-blue-800 dark:text-blue-200',
    message: 'text-blue-700 dark:text-blue-300',
    closeButton: 'text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-800/50 focus:ring-blue-500',
    progressBg: 'bg-blue-100 dark:bg-blue-800/30',
    progress: 'bg-blue-500 dark:bg-blue-400'
  }
}

// Default durations per type (must match useToast)
const durations = {
  [TOAST_TYPES.SUCCESS]: 4000,
  [TOAST_TYPES.ERROR]: 6000,
  [TOAST_TYPES.WARNING]: 5000,
  [TOAST_TYPES.INFO]: 4000
}

function getDuration(type) {
  return durations[type] || 4000
}

function getIcon(type) {
  const icons = {
    [TOAST_TYPES.SUCCESS]: CheckCircleIcon,
    [TOAST_TYPES.ERROR]: XCircleIcon,
    [TOAST_TYPES.WARNING]: ExclamationTriangleIcon,
    [TOAST_TYPES.INFO]: InformationCircleIcon
  }
  return icons[type] || InformationCircleIcon
}
</script>

<style scoped>
@keyframes shrink {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}
</style>
